import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '', inquiry_type: 'general' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, form);
      toast.success('Message sent! We\'ll get back to you soon.');
      setForm({ name: '', email: '', company: '', message: '', inquiry_type: 'general' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const inputClass = "bg-[#111122] border-white/10 text-white placeholder:text-slate-600 focus:border-[#00d4ff] focus:ring-[#00d4ff]/20 h-11";

  return (
    <div className="bg-[#0a0a1a] min-h-screen">
      <section data-testid="contact-page" className="relative py-24 md:py-32">
        <div className="glow-spot w-[300px] h-[300px] bg-[#00d4ff] top-[-50px] right-[10%] opacity-10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.2em] uppercase text-[#00d4ff] font-bold mb-4">Contact</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Get in touch
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-slate-400">Have questions? We'd love to hear from you.</motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="lg:col-span-2">
              <form data-testid="contact-form" onSubmit={handleSubmit} className="glass-card p-8 md:p-10 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div variants={fadeUp}>
                    <Label className="text-slate-300 text-sm mb-2 block">Name *</Label>
                    <Input data-testid="contact-name-input" placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputClass} />
                  </motion.div>
                  <motion.div variants={fadeUp}>
                    <Label className="text-slate-300 text-sm mb-2 block">Email *</Label>
                    <Input data-testid="contact-email-input" type="email" placeholder="you@company.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inputClass} />
                  </motion.div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <motion.div variants={fadeUp}>
                    <Label className="text-slate-300 text-sm mb-2 block">Company</Label>
                    <Input data-testid="contact-company-input" placeholder="Your company" value={form.company} onChange={e => setForm({...form, company: e.target.value})} className={inputClass} />
                  </motion.div>
                  <motion.div variants={fadeUp}>
                    <Label className="text-slate-300 text-sm mb-2 block">Inquiry Type</Label>
                    <Select value={form.inquiry_type} onValueChange={v => setForm({...form, inquiry_type: v})}>
                      <SelectTrigger data-testid="contact-inquiry-select" className={inputClass}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111122] border-white/10">
                        <SelectItem value="general">General Inquiry</SelectItem>
                        <SelectItem value="demo">Book a Demo</SelectItem>
                        <SelectItem value="support">Technical Support</SelectItem>
                        <SelectItem value="partnership">Partnership</SelectItem>
                        <SelectItem value="enterprise">Enterprise Sales</SelectItem>
                      </SelectContent>
                    </Select>
                  </motion.div>
                </div>
                <motion.div variants={fadeUp}>
                  <Label className="text-slate-300 text-sm mb-2 block">Message *</Label>
                  <Textarea data-testid="contact-message-input" placeholder="Tell us how we can help..." rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className={`${inputClass} min-h-[120px]`} />
                </motion.div>
                <motion.div variants={fadeUp}>
                  <Button data-testid="contact-submit-btn" type="submit" disabled={loading} className="bg-gradient-to-r from-[#00d4ff] to-[#3b82f6] text-[#0a0a1a] font-semibold rounded-full px-8 py-3 h-12 hover:opacity-90 shadow-[0_0_20px_rgba(0,212,255,0.3)] disabled:opacity-50">
                    {loading ? 'Sending...' : <><Send className="mr-2 w-4 h-4" /> Send Message</>}
                  </Button>
                </motion.div>
              </form>
            </motion.div>

            {/* Sidebar */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-6">
              {[
                { icon: Mail, title: 'Email', detail: 'hello@g19studios.com', sub: 'We respond within 24 hours' },
                { icon: MapPin, title: 'Office', detail: 'San Francisco, CA', sub: 'Financial District' },
                { icon: Phone, title: 'Phone', detail: '+1 (555) 123-4567', sub: 'Mon-Fri, 9am-6pm PST' },
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp} data-testid={`contact-info-${i}`} className="glass-card p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#00d4ff]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#00d4ff]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>
                    <p className="text-slate-300 text-sm mt-1">{item.detail}</p>
                    <p className="text-slate-500 text-xs mt-1">{item.sub}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
